import * as tm from "transformation-matrix";

interface Point {
  x: number;
  y: number;
}

interface LayoutTransform {
  scaling: number;
  offset: Point;
}

interface KeyPos<T> {
  row: number;
  col: number;
  key: T;
}

export class Key {
  constructor(
    public x: number,
    public y: number,
    public rot: number,
    public size: Point
  ) {}

  private corner(x_offset: number, y_offset: number): Point {
    const mat = tm.compose(
      tm.translate(this.x, this.y),
      tm.rotateDEG(this.rot)
    );
    return tm.applyToPoint(mat, { x: x_offset, y: y_offset });
  }

  top_left(): Point {
    return this.corner(-this.size.x / 2, this.size.y / 2);
  }
  top_right(): Point {
    return this.corner(this.size.x / 2, this.size.y / 2);
  }
  bot_left(): Point {
    return this.corner(-this.size.x / 2, -this.size.y / 2);
  }
  bot_right(): Point {
    return this.corner(this.size.x / 2, -this.size.y / 2);
  }

  // Corners coordinates after rotation: top_left, top_right, bot_right, bot_left
  corners(): [Point, Point, Point, Point] {
    return [
      this.top_left(),
      this.top_right(),
      this.bot_right(),
      this.bot_left(),
    ];
  }

  // Bounding box coordinates: [min, max]
  bounds(): [Point, Point] {
    const corners = this.corners();
    const min = {
      x: corners.map((p) => p.x).reduce((a, b) => Math.min(a, b)),
      y: corners.map((p) => p.y).reduce((a, b) => Math.min(a, b)),
    };
    const max = {
      x: corners.map((p) => p.x).reduce((a, b) => Math.max(a, b)),
      y: corners.map((p) => p.y).reduce((a, b) => Math.max(a, b)),
    };
    return [min, max];
  }

  transform(t: LayoutTransform): Key {
    return new Key(
      this.x * t.scaling + t.offset.x,
      this.y * t.scaling + t.offset.y,
      this.rot,
      { x: this.size.x * t.scaling, y: this.size.y * t.scaling }
    );
  }
}

export function layout_bounds(keys: KeyPos<Key>[]): [Point, Point] {
  return keys
    .map((i) => i.key.bounds())
    .reduce(([min_a, max_a], [min_b, max_b]) => {
      const min = {
        x: Math.min(min_a.x, min_b.x),
        y: Math.min(min_a.y, min_b.y),
      };
      const max = {
        x: Math.max(max_a.x, max_b.x),
        y: Math.max(max_a.y, max_b.y),
      };
      return [min, max];
    });
}

export class Layout {
  constructor(
    // Key matrix keys[row][col]
    // null means that the key does not exist in the keyboard
    public keys: (Key | null)[][]
  ) {}

  iter_all(): KeyPos<Key | null>[] {
    return this.keys
      .map((cols, row) => {
        return cols.map((key, col) => {
          return { row, col, key };
        });
      })
      .flat();
  }

  iter(): KeyPos<Key>[] {
    const non_null = (i: KeyPos<Key | null>): i is KeyPos<Key> =>
      i.key !== null;
    return this.iter_all().filter(non_null);
  }

  iter_transform(t: LayoutTransform): KeyPos<Key>[] {
    return this.iter().map(({ row, col, key }) => {
      return {
        row,
        col,
        key: key.transform(t),
      };
    });
  }

  bounds(): [Point, Point] {
    return layout_bounds(this.iter());
  }

  // Fit area with given size by returning scaling and offset
  fit_size(size: Point): LayoutTransform {
    const [min, max] = this.bounds();
    const width = max.x - min.x;
    const height = max.y - min.y;
    const scaling = Math.min(size.x / width, size.y / height);
    const scaled_bounds = {
      min: { x: min.x * scaling, y: min.y * scaling },
      max: { x: max.x * scaling, y: max.y * scaling },
    };
    const offset = { x: -scaled_bounds.min.x, y: -scaled_bounds.min.y };
    // console.log({
    //   min,
    //   max,
    //   width,
    //   height,
    //   scaling,
    //   scaling_x: size.x / width,
    //   scaling_y: size.y / width,
    //   center,
    //   offset,
    // });
    return { scaling, offset };
  }
}
